import React, { useContext, useEffect, useState } from 'react';
import { Field, SitecoreContext, LinkFieldValue, RichText } from '@sitecore-jss/sitecore-jss-react';
import { MdArrowBack } from 'react-icons/md';
import './index.scss';
import { isLoggedIn } from '../../utils/authHelper';
import BackToTopButton from '../BackToTopButton/BackToTopButton';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
} from '@mui/material';
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import ToastNotification from '../ToastNotification/ToastNotification';
import { UnifiedPaymentDataService } from '../../utils/UnifiedPaymentDataService';

export interface UnifiedPaymentPageProps {
  fields: {
    BackToICPRedirection: Field<LinkFieldValue>;
    BackToDashboardRedirection: Field<LinkFieldValue>;
    InvoiceNotice: Field<string>;
    CustomInvoiceNotice: Field<string>;
    ToastMessage: Field<string>;
    InvalidInvoiceMessage: Field<string>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  invoiceIdentifier?: string;
}

export interface InvoiceDetailsSection {
  groupNumber: string;
  groupInvoiceId: string;
  groupNumberInvoiceId: string;
  groupInvoiceDate: string; // ISO string date
  signature?: string;
  unpaidInvoices: UnpaidInvoice[];
}

export interface UnpaidInvoice {
  orderId: string;
  rikOrderId: string;
  totalAmount: number;
  name: string;
  products: {
    productId: string;
    productName: string;
    quantity: number;
    price: number;
    type: string | null;
  }[];
  error?: {
    orderId: string;
    errorCode: string;
    errorMessage: string;
  };
}

const UnifiedPaymentInvoiceSection = (props: UnifiedPaymentPageProps): React.JSX.Element => {
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsSection | null>(null);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [customToastMessage] = useState(props.fields.ToastMessage);

  const params = new URLSearchParams(window.location.search);
  const redirectUrl = params.get('RedirectUrl')
    ? decodeURIComponent(params.get('RedirectUrl')!)
    : null;

  function convertDate(date: string | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  async function fetchInvoiceDetails() {
    try {
      const rawQuery = window.location.search;
      const params = new URLSearchParams(rawQuery);

      const payload = Object.fromEntries(params.entries()) as Record<string, any>;
      payload.UnpaidInvoices = payload.UnpaidInvoices?.split(',').map((s: string) => s.trim());

      const apiUrl = `${process.env.REACT_APP_CART_URL}/checkout/custominvoicedetails`;

      const response = await postAPICall(apiUrl, payload);
      const data = response?.data as InvoiceDetailsSection;

      if (data) {
        setInvoiceDetails(data);

        if (dispatch && props?.invoiceIdentifier) {
          dispatch({
            type: 'INVOICE_UPDATE',
            payload: {
              invoicesSummary: {
                [props.invoiceIdentifier]: {
                  totalAmount:
                    data.unpaidInvoices
                      ?.filter((invoice) => !invoice.error)
                      .reduce((sum, invoice) => sum + invoice.totalAmount, 0) ?? 0,
                  invoiceAmounts: {},
                },
              },
              invoiceCounts: {
                totalCount: data.unpaidInvoices.length,
                lobCounts: { [props?.invoiceIdentifier]: data.unpaidInvoices.length },
              },
              toggleInvoiceSummaryLoad: !state?.toggleInvoiceSummaryLoad,
              groupInvoiceId: {
                [props?.invoiceIdentifier]: data.groupInvoiceId,
              },
              groupNumberInvoiceId: {
                [props?.invoiceIdentifier]: data.groupNumberInvoiceId,
              },
              adminGroupNumber: data.groupNumber,
              hasInvalidInvoices: data.unpaidInvoices.some((invoice) => invoice.error),
            },
          });
        }

        UnifiedPaymentDataService.setPaymentInfo(
          props?.invoiceIdentifier ?? '',
          response.data,
          false
        );
      } else {
        console.log('No data received from middleware.');
      }
    } catch (error) {
      console.error('Error parsing query parameters or fetching invoice details:', error);

      showCustomToast(
        state?.sitecoreDictionaryData && state?.sitecoreDictionaryData.InvoicePaymentError
          ? `${state?.sitecoreDictionaryData.InvoicePaymentError}`
          : 'Invalid Invoice Details'
      );
    }
  }

  const showCustomToast = (message: string) => {
    customToastMessage.value = message;
    setToastMessage(customToastMessage);
    setShowToast(true);
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  useEffect(() => {
    const hasInvalidInvoices = invoiceDetails?.unpaidInvoices.some((invoice) => invoice.error);

    if (hasInvalidInvoices) {
      showCustomToast(
        props?.fields?.InvalidInvoiceMessage?.value ||
          'Payment cannot be processed at this time for invoice(s) in the "Invalid Invoices" section below. Please click on the "Back to ICP" button to return to the Unpaid Invoices page in the ICP Portal to review or reject the affected invoice(s).'
      );
    }
  }, [invoiceDetails]);

  if (!isLoggedIn()) return <></>;

  const validInvoices = invoiceDetails?.unpaidInvoices.filter((invoice) => !invoice.error) || [];
  const invalidInvoices = invoiceDetails?.unpaidInvoices.filter((invoice) => invoice.error) || [];

  return (
    <div className="unifiedpaymentinvoicesection">
      <ToastNotification
        fields={{
          ToastMessage: toastMessage,
        }}
        showToast={showToast}
        removeToast={toastRemove}
        toastStatus="error"
      ></ToastNotification>
      <h1 className="invoicePageTitle">Invoice Payment</h1>

      {invoiceDetails && (
        <>
          <a
            href={
              invoiceDetails.groupInvoiceId.length > 0
                ? redirectUrl || props?.fields?.BackToICPRedirection?.value?.href
                : props?.fields?.BackToDashboardRedirection?.value?.href
            }
            target={
              invoiceDetails.groupInvoiceId.length > 0
                ? props?.fields?.BackToICPRedirection?.value?.target
                : props?.fields?.BackToDashboardRedirection?.value?.target
            }
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', width: '100%' }}
          >
            <Button color="secondary" variant="contained" className="backtoICPButton">
              <MdArrowBack style={{ marginRight: '8px', fontSize: '22px' }} />
              {invoiceDetails.groupInvoiceId.length > 0
                ? props?.fields?.BackToICPRedirection?.value?.text
                : props?.fields?.BackToDashboardRedirection?.value?.text}
            </Button>
          </a>

          {invoiceDetails.groupInvoiceId.length > 0 ? (
            <RichText field={props?.fields?.InvoiceNotice} />
          ) : (
            <RichText field={props?.fields?.CustomInvoiceNotice} />
          )}

          {validInvoices.length > 0 && (
            <div className="invoice-table-section">
              <Typography variant="h6" gutterBottom className="unpaidInvoiceTitle">
                Unpaid Invoices
              </Typography>

              <TableContainer component={Paper} className="invoiceTableContainer">
                <Typography variant="body1" gutterBottom>
                  <p className="header-details">
                    <span>
                      <strong>Group Invoice #:</strong> {invoiceDetails.groupInvoiceId}
                    </span>
                    <span>
                      <strong>Group Invoice Date:</strong>{' '}
                      {convertDate(invoiceDetails.groupInvoiceDate)}
                    </span>
                  </p>
                </Typography>

                <div className="invoiceTableScrollWrapper">
                  <Table className="invoiceTable">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Invoice #</TableCell>
                        <TableCell style={{ width: '20%' }}>Order #</TableCell>
                        <TableCell style={{ width: '25%' }}>Program</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validInvoices.map((invoice, index) => (
                        <TableRow
                          key={invoice.rikOrderId}
                          className={index % 2 === 0 ? 'tableRowEven' : 'tableRowOdd'}
                        >
                          <TableCell>{invoice.name}</TableCell>
                          <TableCell className="textBold">
                            <div className="invoiceNumber">{invoice.orderId}</div>
                          </TableCell>
                          <TableCell style={{ width: '20%' }}>{invoice.rikOrderId}</TableCell>
                          <TableCell style={{ width: '25%' }}>
                            {invoice.products[0]?.productName}
                          </TableCell>
                          <TableCell align="right">${invoice.totalAmount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="grandTotalContainer">
                  <Typography variant="body1" className="grandTotalAmount">
                    Grand Total Amount: $
                    {validInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0).toFixed(2)}
                  </Typography>
                </div>
              </TableContainer>
            </div>
          )}

          {invalidInvoices.length > 0 && (
            <div className="invoice-table-section invalidInvoicesSection">
              <Typography variant="h6" gutterBottom className="unpaidInvoiceTitle">
                Invalid Invoices
              </Typography>

              {toastMessage?.value && <p className="invalidInvoicesAlert">{toastMessage.value}</p>}

              <TableContainer component={Paper} className="invoiceTableContainer">
                <div className="invoiceTableScrollWrapper">
                  <Table className="invoiceTable">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invalidInvoices.map((invoice, index) => (
                        <TableRow
                          key={invoice.orderId}
                          className={index % 2 === 0 ? 'tableRowEven' : 'tableRowOdd'}
                        >
                          <TableCell>{invoice.orderId}</TableCell>
                          <TableCell className="invalidInvoiceCell">
                            {invoice.error?.errorCode &&
                            state?.sitecoreDictionaryData?.[invoice.error.errorCode]
                              ? state.sitecoreDictionaryData[invoice.error.errorCode]
                              : invoice.error?.errorMessage}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TableContainer>
            </div>
          )}
        </>
      )}

      <BackToTopButton />
    </div>
  );
};

export default UnifiedPaymentInvoiceSection;
